/**
 * Checks if the device has internet connectivity by attempting to fetch from a set of known URLs.
 * This function sends out fetch requests to multiple URLs simultaneously and returns `true` if
 * any of the requests succeed. It also ensures that unnecessary requests are aborted if one succeeds.
 *
 * Uses Promise.any() to resolve when the first successful request completes, or rejects if all
 * requests either fail or time out.
 *
 * Internal details:
 * - `fetchWithTimeout`: A helper function that wraps the fetch request with a timeout mechanism.
 *   - `url` {string}: The URL to send the request to.
 *   - `timeout` {number}: Maximum time (in milliseconds) to wait for the request before timing out.
 *                         Defaults to 10,000 ms (10 seconds).
 *   - `controller` {AbortController}: A controller used to abort the request if necessary.
 *
 * Timeout handling:
 * - If a request does not complete within the specified timeout, the request is aborted using
 *   `AbortController` and a "Timeout" error is thrown.
 *
 * Abort strategy:
 * - If one of the requests succeeds, the remaining ongoing requests are aborted to conserve resources.
 * - If all requests fail, the remaining requests are aborted as well, and the function returns `false`.
 *
 * Error handling:
 * - Catches any errors that occur during the fetch operations, including timeouts.
 * - Returns `false` if all requests either fail or timeout.
 */

export const checkInternet = async () => {
  const fetchWithTimeout = (url, timeout = 10000, controller) => {
    return Promise.race([
      fetch(url, { mode: 'no-cors', signal: controller.signal }), // Fetch request with abort signal
      new Promise((_, reject) =>
        setTimeout(() => {
          reject(new Error('Timeout'));
          controller.abort(); // Cancel the request on timeout
        }, timeout)
      ),
    ]);
  };

  const urls = ['https://www.google.com', 'https://www.cloudflare.com', 'https://www.amazon.com'];

  // Create an AbortController for each request
  const controllers = urls.map(() => new AbortController());

  // Send requests to all URLs simultaneously
  const promises = urls.map((url, index) => fetchWithTimeout(url, 10000, controllers[index]));

  try {
    await Promise.any(promises);
    // If any request succeeds, abort the remaining requests
    controllers.forEach((controller) => controller.abort());
    return true; // Return true when one request succeeds
  } catch (err) {
    // If all requests fail or timeout, abort all remaining requests
    controllers.forEach((controller) => controller.abort());
    return false; // Return false if all requests fail or time out
  }
};
